/**
 * TreasuryReportCard — "where is the money sitting right now?" view.
 *
 * Shows for the selected day (or rolling window):
 *   • Per-method IN/OUT/NET (which Cash App, Venmo, Zelle, Cash drawer, etc.
 *     account each dollar passed through)
 *   • Total IN, OUT, and NET cash position change
 *   • Free plays issued today (informational — no real money)
 *   • Per-cashier breakdown (who handled what)
 *
 * Lives at the very top of the Admin → Reports tab so it's the first thing
 * the operator sees when closing out the day.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowDownLeft, ArrowUpRight, Wallet, Gift, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const METHOD_TONE = {
    'Cash':         'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
    'Cash App':     'border-sky-500/40 bg-sky-500/10 text-sky-200',
    'Chime':        'border-lime-500/40 bg-lime-500/10 text-lime-200',
    'PayPal':       'border-blue-500/40 bg-blue-500/10 text-blue-200',
    'Venmo':        'border-indigo-500/40 bg-indigo-500/10 text-indigo-200',
    'Zelle':        'border-purple-500/40 bg-purple-500/10 text-purple-200',
    'Apple Pay':    'border-zinc-300/30 bg-zinc-200/10 text-zinc-100',
    'Square':       'border-slate-400/40 bg-slate-400/10 text-slate-200',
    'Other':        'border-zinc-700 bg-zinc-900 text-zinc-200',
    'Legacy':       'border-stone-600/40 bg-stone-700/10 text-stone-300',
    'Unspecified':  'border-rose-500/40 bg-rose-500/10 text-rose-200',
};

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayISO = () => {
    // ISO date in CT — matches the backend's report window boundaries
    const now = new Date();
    const ct = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    ct.setHours(ct.getHours() - 7); // business day runs 7 AM → 7 AM Central
    const yyyy = ct.getFullYear();
    const mm = String(ct.getMonth() + 1).padStart(2, '0');
    const dd = String(ct.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const shiftDate = (iso, deltaDays) => {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + deltaDays);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const TreasuryReportCard = () => {
    const [date, setDate] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/admin/treasury-report`, {
                    params: { date }, withCredentials: true,
                });
                if (!cancelled) setData(data);
            } catch {
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [date]);

    if (loading || !data) {
        return (
            <div className="card-dark p-6" data-testid="treasury-report-card">
                <p className="text-xs text-[#A0AAB2]">Loading treasury report…</p>
            </div>
        );
    }

    const { totals, locations, free_plays, free_plays_total, free_plays_count, cashiers, window } = data;
    const isToday = date === todayISO();

    return (
        <div className="card-dark p-6 space-y-5" data-testid="treasury-report-card">
            {/* Header — date selector + label */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Wallet className="w-5 h-5 text-[#FFD700]" />
                    <div>
                        <h3 className="text-base font-bold text-white">End-of-Day Treasury Report</h3>
                        <p className="text-[10px] text-[#A0AAB2] uppercase tracking-wide">
                            {window.label} · {isToday ? 'Live' : 'Final'} · 7 AM–7 AM Central · same figures as Money summary
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1.5" data-testid="treasury-date-controls">
                    <Button size="sm" variant="outline"
                        onClick={() => setDate(shiftDate(date, -1))}
                        className="h-8 w-8 p-0 border-white/10 text-white"
                        data-testid="treasury-prev-day">
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <input type="date" value={date} max={todayISO()}
                        onChange={(e) => setDate(e.target.value)}
                        className="h-8 px-2 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-xs font-mono"
                        data-testid="treasury-date-input" />
                    <Button size="sm" variant="outline"
                        onClick={() => setDate(shiftDate(date, 1))}
                        disabled={isToday}
                        className="h-8 w-8 p-0 border-white/10 text-white disabled:opacity-40"
                        data-testid="treasury-next-day">
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                    {!isToday && (
                        <Button size="sm" variant="outline"
                            onClick={() => setDate(todayISO())}
                            className="h-8 px-2 border-white/10 text-white text-xs"
                            data-testid="treasury-today">
                            <Calendar className="w-3 h-3 mr-1" /> Today
                        </Button>
                    )}
                </div>
            </div>

            {/* Headline totals */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <SummaryTile label="Total IN" amount={totals.in} icon={ArrowDownLeft}
                    accent="text-emerald-400" testId="treasury-total-in" />
                <SummaryTile label="Total OUT" amount={totals.out} icon={ArrowUpRight}
                    accent="text-rose-400" testId="treasury-total-out" />
                <SummaryTile label="NET TODAY" amount={totals.net}
                    accent={totals.net >= 0 ? 'text-[#FFD700]' : 'text-rose-300'}
                    highlight testId="treasury-total-net" />
                <SummaryTile label="Free Play Issued" amount={free_plays_total}
                    icon={Gift} accent="text-pink-300"
                    extra={`${free_plays_count} grants`} testId="treasury-free-play" />
            </div>

            {/* Locations — where the money actually is */}
            <div>
                <h4 className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">
                    Where the money is — by payment location
                </h4>
                {locations.length === 0 ? (
                    <p className="text-xs text-[#A0AAB2] py-3 text-center">No money movement on this day.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2"
                        data-testid="treasury-locations">
                        {locations.map(l => (
                            <div key={l.method}
                                className={`rounded-md border ${METHOD_TONE[l.method] || METHOD_TONE['Other']} p-3`}
                                data-testid={`treasury-location-${l.method.toLowerCase().replace(/\s+/g, '-')}`}>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-bold uppercase tracking-wide">{l.method}</span>
                                    <span className={`text-base font-mono font-bold ${l.net >= 0 ? '' : 'text-rose-300'}`}>
                                        {l.net >= 0 ? '+' : ''}{fmt(l.net)}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-[10px] opacity-80">
                                    <div>
                                        <span className="opacity-60">IN</span>{' '}
                                        <span className="font-mono">{fmt(l.in)}</span>
                                        <span className="opacity-50"> ({l.in_count})</span>
                                    </div>
                                    <div>
                                        <span className="opacity-60">OUT</span>{' '}
                                        <span className="font-mono">{fmt(l.out)}</span>
                                        <span className="opacity-50"> ({l.out_count})</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Per-cashier breakdown */}
            {cashiers.length > 0 && (
                <div>
                    <h4 className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">
                        Per staff member (loads recorded · cashouts paid)
                    </h4>
                    <div className="overflow-x-auto rounded-md border border-white/10" data-testid="treasury-cashiers">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-white/5 text-[10px] uppercase tracking-wide text-[#A0AAB2]">
                                    <th className="text-left p-2">Cashier</th>
                                    <th className="text-right p-2">Loads In</th>
                                    <th className="text-right p-2">Cashouts Out</th>
                                    <th className="text-right p-2">Net</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cashiers.map(c => (
                                    <tr key={c.cashier_id || c.cashier_name} className="border-t border-white/5">
                                        <td className="p-2 text-white font-bold">{c.cashier_name}</td>
                                        <td className="p-2 text-right font-mono text-emerald-300">
                                            {fmt(c.loads_in)}
                                            <span className="text-[9px] text-zinc-500 ml-1">({c.loads_count})</span>
                                        </td>
                                        <td className="p-2 text-right font-mono text-rose-300">
                                            {fmt(c.cashouts_out)}
                                            <span className="text-[9px] text-zinc-500 ml-1">({c.cashouts_count})</span>
                                        </td>
                                        <td className={`p-2 text-right font-mono font-bold ${c.net >= 0 ? 'text-[#FFD700]' : 'text-rose-300'}`}>
                                            {c.net >= 0 ? '+' : ''}{fmt(c.net)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Free plays detail (informational) */}
            {free_plays.length > 0 && (
                <div>
                    <h4 className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">
                        Free plays issued (informational — no real cash)
                    </h4>
                    <div className="flex flex-wrap gap-2" data-testid="treasury-free-plays">
                        {free_plays.map(f => (
                            <div key={f.kind} className="rounded-md border border-pink-500/20 bg-pink-500/5 px-3 py-2">
                                <p className="text-[10px] text-pink-300 uppercase tracking-wide font-bold">{f.kind.replace(/_/g, ' ')}</p>
                                <p className="text-sm font-mono font-bold text-white">{fmt(f.total)}</p>
                                <p className="text-[9px] text-zinc-500">{f.count} grants</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const SummaryTile = ({ label, amount, icon: Icon, accent, extra, highlight, testId }) => (
    <div className={`rounded-md p-3 ${highlight ? 'bg-[#FFD700]/5 border border-[#FFD700]/30' : 'bg-[#0a0a0a] border border-white/10'}`}
        data-testid={testId}>
        <div className="flex items-center gap-1.5 mb-1">
            {Icon && <Icon className={`w-3.5 h-3.5 ${accent}`} />}
            <p className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">{label}</p>
        </div>
        <p className={`text-xl font-mono font-bold ${accent}`}>{fmt(amount)}</p>
        {extra && <p className="text-[10px] text-zinc-500">{extra}</p>}
    </div>
);

export default TreasuryReportCard;
